import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tooltip,
  Divider,
  Stack
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import {
  StyledTableContainer,
  TableSkeleton,
  FilterBar,
  FilterSelect,
  StyledPagination,
  EmptyState
} from '../components/TableShared';
import api from '../config/api';

const ff = "'Poppins', sans-serif";

const RATING_OPTIONS = [
  { value: 0, label: 'All Ratings' },
  { value: 5, label: '5 Stars' },
  { value: 4, label: '4 Stars' },
  { value: 3, label: '3 Stars' },
  { value: 2, label: '2 Stars' },
  { value: 1, label: '1 Star' },
];

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalReviews, setTotalReviews] = useState(0);

  // Rating filter (server-side)
  const [ratingFilter, setRatingFilter] = useState(0);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Detail dialog state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailReview, setDetailReview] = useState(null);

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page: page + 1, limit: rowsPerPage };
      if (ratingFilter > 0) params.rating = ratingFilter;
      const response = await api.get('/admin/reviews', { params });
      const data = response.data.data || response.data;
      setReviews(data.reviews || []);
      setTotalReviews(data.total || data.reviews?.length || 0);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError('Failed to load reviews. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, ratingFilter]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const filteredReviews = reviews;

  const handleDeleteClick = (review) => {
    setReviewToDelete(review);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reviewToDelete) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/admin/reviews/${reviewToDelete.id}`);
      setDeleteDialogOpen(false);
      setReviewToDelete(null);
      fetchReviews();
    } catch (err) {
      console.error('Error deleting review:', err);
      setError('Failed to delete review. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setReviewToDelete(null);
  };

  const handleDetailOpen = (review) => {
    setDetailReview(review);
    setDetailOpen(true);
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
    setDetailReview(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRatingFilterChange = (e) => {
    setRatingFilter(e.target.value);
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getReviewerName = (review) => {
    if (review.user) return review.user.fullName || review.user.email || 'Unknown';
    if (review.reviewer) return review.reviewer.fullName || review.reviewer.email || 'Unknown';
    return 'N/A';
  };

  const getReviewerEmail = (review) => {
    if (review.user) return review.user.email || '';
    if (review.reviewer) return review.reviewer.email || '';
    return '';
  };

  const getSitterName = (review) => {
    if (review.sitter) return review.sitter.fullName || review.sitter.email || review.sitterId || 'N/A';
    if (review.sitterName) return review.sitterName;
    return review.sitterId || 'N/A';
  };

  const getCommentText = (review) => review.comment || review.text || '';

  return (
    <Box sx={{ fontFamily: ff }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filter Bar */}
      <FilterBar>
        <FilterSelect
          label="Rating"
          value={ratingFilter}
          onChange={handleRatingFilterChange}
          options={RATING_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label,
          }))}
          minWidth={160}
        />
        {ratingFilter !== 0 && (
          <Box sx={{ fontFamily: ff, fontSize: 12, color: '#718096' }}>
            Showing {filteredReviews.length} of {reviews.length} reviews on this page
          </Box>
        )}
      </FilterBar>

      {loading ? (
        <TableSkeleton columns={6} rows={6} />
      ) : (
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Reviewer</TableCell>
                <TableCell>Sitter</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Comment</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReviews.map((review) => (
                <TableRow key={review.id}>
                  <TableCell>
                    <Box sx={{ fontWeight: 500 }}>
                      {getReviewerName(review)}
                    </Box>
                  </TableCell>
                  <TableCell>{getSitterName(review)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Rating value={review.rating || 0} readOnly size="small" />
                      <Box sx={{ fontSize: 11, color: '#718096' }}>
                        ({review.rating || 0})
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    {getCommentText(review) ? (
                      <Tooltip title={getCommentText(review)} placement="top-start" arrow>
                        <Box
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 300,
                            cursor: 'pointer',
                          }}
                          onClick={() => handleDetailOpen(review)}
                        >
                          {getCommentText(review)}
                        </Box>
                      </Tooltip>
                    ) : (
                      <Box sx={{ color: '#A0AEC0', fontStyle: 'italic' }}>
                        No comment
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(review.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View details">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleDetailOpen(review)}
                        sx={{ mr: 0.5 }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete review">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(review)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filteredReviews.length === 0 && (
                <EmptyState
                  colSpan={6}
                  message={
                    ratingFilter !== 0
                      ? `No ${ratingFilter}-star reviews on this page.`
                      : 'No reviews found.'
                  }
                />
              )}
            </TableBody>
          </Table>
          <StyledPagination
            count={totalReviews}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </StyledTableContainer>
      )}

      {/* Review Detail Dialog */}
      <Dialog open={detailOpen} onClose={handleDetailClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0, fontFamily: ff }}>Review Details</DialogTitle>
        <DialogContent>
          {detailReview && (
            <Box sx={{ pt: 2, fontFamily: ff }}>
              {/* Rating */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Rating value={detailReview.rating || 0} readOnly />
                <Box sx={{ fontSize: 18, fontWeight: 600 }}>
                  {detailReview.rating || 0}/5
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Reviewer Info */}
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Box>
                  <Box sx={{ fontSize: 11, color: '#718096', mb: 0.25 }}>Reviewer</Box>
                  <Box sx={{ fontWeight: 500 }}>
                    {getReviewerName(detailReview)}
                  </Box>
                  {getReviewerEmail(detailReview) && getReviewerEmail(detailReview) !== getReviewerName(detailReview) && (
                    <Box sx={{ fontSize: 12, color: '#718096' }}>
                      {getReviewerEmail(detailReview)}
                    </Box>
                  )}
                </Box>
                <Box>
                  <Box sx={{ fontSize: 11, color: '#718096', mb: 0.25 }}>Sitter</Box>
                  <Box sx={{ fontWeight: 500 }}>
                    {getSitterName(detailReview)}
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ fontSize: 11, color: '#718096', mb: 0.25 }}>Date</Box>
                  <Box sx={{ fontSize: 13 }}>
                    {formatDateTime(detailReview.createdAt)}
                  </Box>
                </Box>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {/* Comment */}
              <Box sx={{ fontWeight: 600, fontSize: 13, mb: 1 }}>Comment</Box>
              <Box sx={{ p: 2, bgcolor: '#F7FAFC', borderRadius: '8px', border: '1px solid #EDF2F7' }}>
                <Box sx={{ fontSize: 13, whiteSpace: 'pre-wrap', color: '#2D3748' }}>
                  {getCommentText(detailReview) || 'No comment provided.'}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            sx={{ fontFamily: ff }}
            onClick={() => {
              handleDetailClose();
              if (detailReview) handleDeleteClick(detailReview);
            }}
          >
            Delete
          </Button>
          <Button onClick={handleDetailClose} sx={{ fontFamily: ff }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle sx={{ fontFamily: ff }}>Delete Review</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: ff }}>
            Are you sure you want to delete this review? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading} sx={{ fontFamily: ff }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading} sx={{ fontFamily: ff }}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
